import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, BarChart3, Share2, Settings, Layout, Home, User, Briefcase, Mail, LogIn, Users } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import logoMonk from "@/assets/mkstd-branca.png";
import { useCtaButton } from "@/hooks/useCtaButton";

const solucoesItems = [
  { label: "Tráfego Pago", path: "/trafegopago", icon: BarChart3 },
  { label: "Gestão de Rede Social", path: "/redesocial", icon: Share2 },
  { label: "Landing Page", path: "/landingpage", icon: Layout },
  { label: "Sistema de Gestão", path: "/gestaosistema", icon: Settings },
];

const navItems = [
  { label: "Home", path: "/", icon: Home },
  { label: "Sobre", path: "/sobre", icon: User },
  { label: "Soluções", path: "/solucoes", icon: Layout, dropdown: solucoesItems },
  { label: "Portfólio", path: "/portfolio", icon: Briefcase },
  { label: "Contato", path: "/contato", icon: Mail },
  { label: "Área do Cliente", path: "/cliente/login", icon: Users },
];

const Navbar = ({ minimal = false }: { minimal?: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { text: ctaText, link: ctaLink } = useCtaButton();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isActive = (path: string) => {
    if (path === "/solucoes") {
      return location.pathname === "/solucoes" || 
             location.pathname.startsWith("/trafegopago") || 
             location.pathname.startsWith("/redesocial") || 
             location.pathname.startsWith("/landingpage") || 
             location.pathname.startsWith("/gestaosistema");
    }
    return location.pathname === path;
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
          scrolled 
            ? "bg-black/90 backdrop-blur-xl border-white/10 py-3" 
            : "bg-black/40 backdrop-blur-md border-white/5 py-5"
        }`}
        style={{ height: scrolled ? "72px" : "84px" }}
      >
        <div className="max-w-[1280px] mx-auto px-6 h-full flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group flex-shrink-0">
            <img 
              src={logoMonk} 
              alt="Monk Studio" 
              className="w-auto transition-all duration-300 group-hover:scale-105" 
              style={{ height: scrolled ? "38px" : "42px" }}
            />
          </Link>

          {/* Desktop Menu */}
          {!minimal && (
          <div className="hidden lg:flex items-center gap-2">
            {navItems.map((item) =>
              item.dropdown ? (
                <div
                  key={item.path}
                  className="relative"
                  ref={dropdownRef}
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <Link
                    to="/solucoes"
                    className={`flex items-center gap-1.5 px-4 py-2 text-[14px] font-semibold transition-all rounded-full border border-transparent ${
                      isActive(item.path)
                        ? "text-white bg-white/10 border-white/5"
                        : "text-white/75 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {isActive(item.path) && (
                      <motion.span 
                        layoutId="active-pill"
                        className="absolute inset-0 bg-gradient-to-r from-[#FFB800]/10 to-[#FFB800]/5 rounded-full border border-[#FFB800]/20"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-1.5">
                      {item.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
                    </span>
                  </Link>
                  <AnimatePresence>
                    {dropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-3 w-56 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10 overflow-hidden backdrop-blur-2xl bg-black/95 p-1.5"
                      >
                        {item.dropdown.map((sub) => {
                          const Icon = sub.icon;
                          return (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              onClick={() => setDropdownOpen(false)}
                              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-all rounded-xl"
                            >
                              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                <Icon className="w-4 h-4" />
                              </div>
                              {sub.label}
                            </Link>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 text-[14px] font-semibold transition-all rounded-full border border-transparent ${
                    isActive(item.path)
                      ? "text-white"
                      : "text-white/75 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {isActive(item.path) && (
                    <motion.span 
                      layoutId="active-pill"
                      className="absolute inset-0 bg-white/10 rounded-full border border-white/5"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10">{item.label}</span>
                </Link>
              )
            )}
          </div>
          )}

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#FFB800] via-[#FF7A00] to-[#FF5C7A] px-7 py-3 text-sm font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(255,122,0,0.4)]"
            >
              <span className="flex items-center gap-2">
                {ctaText}
                <FaWhatsapp className="w-4.5 h-4.5" />
              </span>
            </a>
          </div>

          {/* Mobile Menu Controls */}
          <div className="lg:hidden flex items-center gap-4">
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#FFB800] via-[#FF7A00] to-[#FF5C7A] px-4 py-2 text-[12px] font-bold text-white shadow-lg shadow-orange-500/20"
            >
              {minimal && <span>Entre em Contato</span>}
              <FaWhatsapp className="w-3.5 h-3.5" />
            </a>
            {!minimal && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            )}
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -20 }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed top-4 left-4 right-4 max-h-[calc(100vh-32px)] bg-[#0D0D0D] border border-white/10 rounded-[32px] z-50 lg:hidden p-8 flex flex-col shadow-[0_40px_100px_rgba(0,0,0,0.8)] overflow-hidden"
              >
                {/* Header within menu */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex flex-col">
                    <span className="text-white font-black text-2xl leading-none tracking-tight">monk</span>
                    <span className="text-[10px] font-bold tracking-[0.4em] text-white/40 uppercase bg-gradient-to-r from-[#FFB800] to-[#FF5C7A] bg-clip-text text-transparent">STUDIO</span>
                  </div>
                  <button 
                    onClick={() => setIsOpen(false)}
                    className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-1.5 pr-2 -mr-2 scrollbar-none">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return item.dropdown ? (
                      <div key={item.path} className="space-y-1">
                        <button
                          onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                          className={`flex items-center justify-between w-full px-5 py-4 rounded-2xl text-base font-semibold transition-all relative ${
                            isActive(item.path) 
                              ? "text-[#FFB800] bg-white/[0.03]" 
                              : "text-white/70 hover:bg-white/[0.02]"
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <Icon className={`w-5 h-5 ${isActive(item.path) ? "text-[#FFB800]" : "text-white/40"}`} />
                            {item.label}
                          </div>
                          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobileDropdownOpen ? 'rotate-180' : ''} text-white/20`} />
                        </button>
                        <AnimatePresence>
                          {mobileDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-14 pr-4 space-y-1"
                            >
                              {item.dropdown.map((sub) => (
                                <Link
                                  key={sub.path}
                                  to={sub.path}
                                  className="block py-3 text-sm font-medium text-white/40 hover:text-white transition-colors"
                                >
                                  {sub.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-4 px-5 py-4 rounded-2xl text-base font-semibold transition-all relative ${
                          isActive(item.path) 
                            ? "text-[#FFB800] bg-white/[0.03]" 
                            : "text-white/70 hover:bg-white/[0.02]"
                        }`}
                      >
                        {isActive(item.path) && (
                          <motion.div 
                            layoutId="mobile-active-bg"
                            className="absolute inset-0 bg-white/[0.03] rounded-2xl border border-white/5"
                          />
                        )}
                        <Icon className={`w-5 h-5 relative z-10 ${isActive(item.path) ? "text-[#FFB800]" : "text-white/40"}`} />
                        <span className="relative z-10">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>

                <div className="mt-8 pt-2">
                  <a
                    href={ctaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 w-full rounded-full bg-gradient-to-r from-[#FFB800] via-[#FF7A00] to-[#FF5C7A] py-5 text-base font-bold text-white shadow-xl shadow-orange-500/20"
                  >
                    Entre em Contato!
                    <FaWhatsapp className="w-5 h-5" />
                  </a>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
